import axios from 'axios';
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, X } from "lucide-react";
import { formatCurrency } from "@/lib/config";
import api from "@/lib/api";

export const CategoryDetailModal = ({ open, onClose, category, year, month, departement }) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (open && category) {
      loadTransactions();
    }
  }, [open, category, year, month, departement]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category: category,
      });
      
      if (departement && departement !== "GLOBAL") {
        params.append("departement", departement);
      }

      const response = await axios.get(
        `${api.baseUrl}/expenses/by-category/${year}/${month}?${params}`
      );
      const data = response.data;

      // Charger aussi les règles de taxes pour cette catégorie
      const taxesRes = await axios.get(`${api.baseUrl}/taxes/rules?categorie=${encodeURIComponent(category)}`);
      const taxesData = taxesRes.data;
      
      const taxRule = taxesData.rules && taxesData.rules.length > 0 ? taxesData.rules[0] : null;
      const tpsRate = taxRule ? taxRule.tps : 0.05;
      const tvqRate = taxRule ? taxRule.tvq : 0.09975;
      const totalTaxRate = tpsRate + tvqRate;
      
      // Calculer montants HT pour chaque transaction
      const transactionsWithHT = (data.transactions || []).map(tx => {
        const ttc = tx.montant;
        const ht = totalTaxRate > 0 ? ttc / (1 + totalTaxRate) : ttc;
        const tps = ht * tpsRate;
        const tvq = ht * tvqRate;
        
        return {
          ...tx,
          montant_ht: ht,
          montant_ttc: ttc,
          tps: tps,
          tvq: tvq
        };
      });

      setTransactions(transactionsWithHT);
      
      // Total HT
      const totalHT = transactionsWithHT.reduce((sum, tx) => sum + tx.montant_ht, 0);
      setTotalAmount(totalHT);
    } catch (error) {
      console.error("Erreur chargement transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Transactions - {category}</span>
            <button
              onClick={onClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
          <DialogDescription>
            {departement && departement !== "GLOBAL" ? `Département: ${departement} • ` : ""}
            {transactions.length} transaction{transactions.length > 1 ? 's' : ''} • Total: {formatCurrency(totalAmount)}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Date</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Description</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Compte</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Ventilation</th>
                    <th className="py-2 px-3 text-right font-semibold text-slate-600">Montant</th>
                    <th className="py-2 px-3 text-center font-semibold text-slate-600">Auto</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr key={tx.id || tx._id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-600">
                        {new Date(tx.date).toLocaleDateString('fr-CA')}
                      </td>
                      <td className="py-2 px-3">
                        <div className="max-w-md">
                          <p className="font-medium text-slate-900 truncate">
                            {tx.name || "—"}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {tx.memo || "—"}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600 font-mono">
                        {tx.account}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {tx.ventilation.map((v, i) => (
                            <Badge key={v.id || v._id || i} variant="outline" className="text-xs">
                              {v.dept}: {(v.pct * 100).toFixed(0)}%
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        <div className="text-red-600">
                          {formatCurrency(tx.montant_ht)}
                        </div>
                        <div className="text-xs text-slate-500">
                          TTC: {formatCurrency(tx.montant_ttc)}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {tx.auto_classified ? (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Auto</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 text-xs">Manuel</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-bold">
                    <td colSpan="4" className="py-3 px-3 text-right text-slate-900">
                      TOTAL
                    </td>
                    <td className="py-3 px-3 text-right text-red-600">
                      {formatCurrency(totalAmount)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
